import type { ReactNode } from 'react'
import { Check, ChevronRight, Circle, Repeat, Sparkles, Target } from 'lucide-react'

/**
 * THE row. Every compact task or habit on /today is this component — the
 * "then" rows under the job, the finished list under Done, the Stretch
 * candidates, the rows resting for today.
 *
 * North Star rule 1 (owner, 2026-09-11): *"Same thing → same shape. Every task
 * should use the same task component. Every habit should use the same habit
 * component. If clicking the task opens it, every task should visibly behave
 * that way. Don't sometimes use `Open`, sometimes a text link, and sometimes
 * invisible clickability."* Before this, one page drew a task five ways: an
 * underlined text button, a check + title, an icon tile with a verb, a dashed
 * dimmed box, and a plain line.
 *
 * The contract:
 *   - an icon tile names the KIND (task · habit · goal · skill); a finished
 *     row's tile turns to a check on the ok tint — state is in the tile, never
 *     in a strike-through;
 *   - the title, then ONE meta line built by `taskMeta` (or the row's one
 *     line of context, e.g. the path a stretch item belongs to);
 *   - the whole row opens the item, and SAYS so with a chevron at the end —
 *     unless a verb takes that slot, in which case the verb is the row's one
 *     action and the title still opens;
 *   - `prefix` is the connector word ("then") and never a second style.
 */

export type TodayRowKind = 'task' | 'habit' | 'goal' | 'skill'

const KIND_ICON = {
  task: Circle,
  habit: Repeat,
  goal: Target,
  skill: Sparkles,
} as const

export interface TodayRowProps {
  kind: TodayRowKind
  title: string
  /** One quiet line under the title. Build task rows' with `taskMeta`. */
  meta?: string | null
  /** A connector word before the tile: "then". */
  prefix?: string
  done?: boolean
  /** Resting rows: same shape, lower opacity — never a different box. */
  muted?: boolean
  onOpen?: () => void
  /** The row's one verb. Takes the chevron's slot. Keep it QUIET (outline);
   *  the page's only filled button is "Start this" on the job. */
  trailing?: ReactNode
  testId?: string
}

export function TodayRow({
  kind, title, meta, prefix, done = false, muted = false, onOpen, trailing, testId,
}: TodayRowProps) {
  const Icon = done ? Check : KIND_ICON[kind]
  const tile = done
    ? 'bg-tint-ok-soft text-ok'
    : 'bg-tint-brand-wash text-brand'
  const opens = !!onOpen
  const Body = opens ? 'button' : 'div'
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-2 rounded-xl border border-soft bg-surface pr-2 ${muted ? 'opacity-60' : ''}`}
    >
      {prefix && (
        <span className="pl-3 text-app-meta text-muted shrink-0" aria-hidden="true">{prefix}</span>
      )}
      <Body
        {...(opens ? { type: 'button' as const, onClick: onOpen } : {})}
        className={`flex min-h-touch min-w-0 flex-1 items-center gap-3 rounded-xl py-2 text-left ${prefix ? 'pl-1' : 'pl-3'} ${opens ? 'hover:bg-surface-alt transition-colors' : ''}`}
      >
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tile}`} aria-hidden="true">
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-app-body font-medium text-primary">{title}</span>
          {meta && <span className="block truncate text-app-micro text-muted tabular-nums">{meta}</span>}
        </span>
        {opens && !trailing && (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        )}
      </Body>
      {trailing && <span className="shrink-0">{trailing}</span>}
    </div>
  )
}

/** The quiet verb that sits in a row's trailing slot — one style for all. */
export function RowVerb({
  label, onClick, disabled, busy,
}: { label: string; onClick: () => void; disabled?: boolean; busy?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className="min-h-touch shrink-0 rounded-lg border border-soft bg-surface px-3 text-app-meta font-medium text-secondary hover:bg-surface-alt hover:text-primary disabled:opacity-60 transition-colors"
    >
      {label}
    </button>
  )
}

export default TodayRow
